import React, {useEffect, useState} from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle, FormControl, FormHelperText, Input, InputLabel, MenuItem, Select,
    Typography,
} from "@material-ui/core";
import {useStyles} from "./styles";
import TextField from "@material-ui/core/TextField";
import {deleteConnection} from "../../../../services/codat-service";
import {updateDisconnectMgmtStatus} from "../../../../services/bff-service";
import {setSnackBar} from "../../../../stores/ui/ui-action";
import {useDispatch, useSelector} from "react-redux";
import {Company} from "../../../../stores/company/company-state";

type DMCompany =
    Omit<Company, "hidden" | "userEmail" | "userEntityMappingId" | "isMonthlyRefreshEnabled" | "lastRefreshDate" | "dbPlatform">
    & {
    action_flags?: any[]
};

interface InactivateCompanyDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    company: DMCompany;
}

const InactivateCompanyInstructions: React.FC<InactivateCompanyDialogProps> = ({open, onClose, onConfirm, company}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const user = useSelector((state: any) => state.user);

    const [reason, setReason] = useState(null);
    const [comment, setComment] = useState(null);

    const [errorObj, setErrorObj] = useState<{ reasonMsg: string, commentMsg: string }>({
        reasonMsg: null,
        commentMsg: null
    });

    const itHasActionFlags = Boolean(company?.action_flags?.length)


    useEffect(() => {
        if (open) {
            reset();
        }
    }, [open]);

    const handleChange = ({target: {name = '', value}}) => {
        switch (name) {
            case 'reason':
                setReason(value);

                setErrorObj(prev => ({...prev, reasonMsg: null}));
                if (value === 'CUSTOMER_REQUEST') {
                    setComment(null);
                    setErrorObj(prev => ({...prev, commentMsg: null}));
                }
                break;
            case 'comment':
                setComment(value);

                if (value && value.length >= 10) {
                    setErrorObj(prev => ({...prev, commentMsg: null}));
                }
                break;
        }
    }

    const handleConfirm = async () => {

        if (!itHasActionFlags && runFieldValidations()) {
            return;
        }

        const clonedCompanyInfo = structuredClone(company);
        try {
            // Disconnect the codat connection.
            if (clonedCompanyInfo?.connection_ids && clonedCompanyInfo?.connection_ids?.length) {
                for (const id of clonedCompanyInfo.connection_ids) {
                    const response = await deleteConnection(clonedCompanyInfo.codatCompanyId, id);
                    if (response.status !== 200 && response?.data?.message) {
                        throw new Error(response?.data?.message);
                    }
                }
            }

            let payload: any = {
                status: 'inactive',
                company_id: clonedCompanyInfo.companyId,
                codat_company_id: clonedCompanyInfo.codatCompanyId,
                ...(clonedCompanyInfo.cif && {cif: clonedCompanyInfo.cif}),
                ...(clonedCompanyInfo.crmEntityId && {crm_entity_id: clonedCompanyInfo.crmEntityId}),
                legal_entity_name: clonedCompanyInfo.name,
                codat_connection: clonedCompanyInfo.codatConnectionStatus,
                connection_id: clonedCompanyInfo.connection_ids,
                team_code: clonedCompanyInfo.teamCode,
                ...(clonedCompanyInfo.ct_client_status && {ct_client_status: clonedCompanyInfo.ct_client_status}),
                ...(clonedCompanyInfo.ct_team_code && {ct_team_code: clonedCompanyInfo.ct_team_code}),
                ...(clonedCompanyInfo.cp_total_packages && {cp_total_packages: parseInt(clonedCompanyInfo.cp_total_packages)}),
                ...(clonedCompanyInfo.cp_completed_packages && {cp_completed_packages: parseInt(clonedCompanyInfo.cp_completed_packages)}),
                ...(clonedCompanyInfo.cp_team_code && {cp_team_code: clonedCompanyInfo.cp_team_code}),
                user_email: user.email
            };

            if (itHasActionFlags) {
                payload = {...payload, action_flags: clonedCompanyInfo.action_flags};
            } else {
                const actionFlags = reason === 'CUSTOMER_REQUEST'
                    ? [{customer_request: "Customer Request"}]
                    : [{other: 'Other'}]
                payload = {...payload, action_flags: actionFlags, comment: comment};
            }
            await updateDisconnectMgmtStatus(payload);
            dispatch(setSnackBar({show: true, message: `Company set to "Inactive".`, messageType: "success"}));
        } catch (error: any) {
            dispatch(setSnackBar({
                show: true,
                message: `Failed to update client status (${error.message}). Please try again.`,
                messageType: "error"
            }));
        } finally {
            onConfirm();
        }
    }

    /**
     * The function is used to validate the reason and comment input field.
     * The function will return true when the reason is OTHER and comment char length is less than 10.
     * @return boolean
     */
    const runFieldValidations = () => {
        if (!reason) {
            setErrorObj({reasonMsg: 'Please select a reason.', commentMsg: null});
            return true;
        }
        if (reason === "OTHER" && (!comment || comment?.length < 10)) {
            setErrorObj({reasonMsg: null, commentMsg: 'Please enter at least 10 characters'});
            return true;
        }
        setErrorObj({reasonMsg: null, commentMsg: null});
        return false;

    }

    const reset = () => {
        setReason(null)
        setComment(null)
        setErrorObj({
            reasonMsg: null,
            commentMsg: null
        })
    }

    return (
        <Dialog
            open={open}
            data-testid="dm-inactivate-company-instruction-modal"
            onClose={onClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            maxWidth="xs"
            classes={{paper: classes.dialogPaper}}
        >
            <DialogTitle>
                <Typography variant="h6" component="h2">
                    Update Client Status to Inactive?
                </Typography>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <Typography variant="body1">
                    Updating {company?.name}’s status to "<strong>Inactive</strong>" will:
                    <ul className={classes.list}>
                        <li>Delete their Codat connection</li>
                        <li>Disable monthly refresh</li>
                        <li>Disable their reminder email</li>
                    </ul>
                </Typography>

                {!itHasActionFlags &&
                    <>
                        <FormControl
                            className={classes.customDropDown}
                            variant="outlined" error={Boolean(errorObj?.reasonMsg)}>
                            <InputLabel id="reason-id">Reason for Inactive *</InputLabel>
                            <Select
                                labelId="reason-id"
                                id="reason-id"
                                name={'reason'}
                                data-testid={'reason-field'}
                                onChange={handleChange}
                                value={reason}
                                className={classes.customDropDown}
                                label="Reason for Inactive *"
                                input={<Input placeholder="Reason for Inactive *"/>}
                                required
                                MenuProps={{
                                    anchorOrigin: {
                                        vertical: "bottom",
                                        horizontal: "center",
                                    },
                                    transformOrigin: {
                                        vertical: "bottom",
                                        horizontal: "center",
                                    },
                                }}
                            >
                                <MenuItem value="CUSTOMER_REQUEST">Customer Request</MenuItem>
                                <MenuItem value="OTHER">Other</MenuItem>
                            </Select>
                            {errorObj?.reasonMsg &&
                                <FormHelperText data-testid={"reason-error-msg"}>{errorObj?.reasonMsg}</FormHelperText>}
                        </FormControl>

                        {reason === "OTHER" && (
                            <FormControl className={classes.commentsInputField}>
                                <TextField
                                    id="outlined-error-helper-text"
                                    label="Please Specify *"
                                    placeholder="Please Specify *"
                                    name={'comment'}
                                    data-testid={'comment-field'}
                                    value={comment}
                                    onChange={handleChange}
                                    error={!!errorObj?.commentMsg}
                                    variant="outlined"
                                    helperText={errorObj?.commentMsg}
                                />
                            </FormControl>
                        )}
                    </>}

            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button className={classes.dialogButton} variant="text" color="primary" onClick={onClose}
                        data-testid={"cancel-btn"}>
                    CANCEL
                </Button>
                <Button className={classes.dialogButton} variant="text" color="primary" onClick={handleConfirm}
                        data-testid={"confirm-btn"}>
                    CONFIRM
                </Button>
            </DialogActions>
        </Dialog>
    )
}
export default InactivateCompanyInstructions;